import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users,
  Check,
  TrendingUp,
  StickyNote,
  Plane,
  Calendar,
  PartyPopper,
  Pin,
  ChevronRight,
  Plus,
  Sun,
  CloudSun,
  Moon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../api/api';
import { notesApi } from '../../../pages/Notes/notesApi';
import WeatherWidgetImpl from './WeatherWidget';
import WidgetHeader from './WidgetHeader';

import { toast } from "../../../utils/toast";
import { hasPermission } from "../../../utils/permissionUtils";
/* Helpers ------------------------------------------------------------- */

const authHeaderFromStorage = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fmtTime = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

/** Time-of-day greeting + matching icon (recomputed as the clock ticks). */
const greetingFor = (date) => {
  const h = date.getHours();
  if (h < 12) return { text: 'Good Morning', Icon: Sun };
  if (h < 17) return { text: 'Good Afternoon', Icon: CloudSun };
  return { text: 'Good Evening', Icon: Moon };
};

/** Prefer the stored first name; fall back to the first word of any name. */
const firstNameOf = (userData, fallback) => {
  const f = (userData?.first_name || '').trim();
  if (f) return f;
  const full = (fallback || '').trim();
  return full ? full.split(/\s+/)[0] : 'there';
};

/* Weather ------------------------------------------------------------- */

export const WeatherWidget = WeatherWidgetImpl;

/* Total Employees ----------------------------------------------------- */

// Joined in the current calendar month — drives the "+N this month" pill.
// `doj` is the joining date the employee form edits; `join_date` is a legacy
// duplicate nothing updates, used only when doj is empty ('0000-00-00' = unset).
const joinedThisMonth = (members) => {
  const now = new Date();
  return members.filter((m) => {
    const raw = [m.doj, m.join_date].find((v) => v && !String(v).startsWith('0000'));
    const d = raw ? new Date(raw) : null;
    return (
      d &&
      !Number.isNaN(d.getTime()) &&
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth()
    );
  }).length;
};

export const TotalEmployeesWidget = () => {
  const [total, setTotal] = useState(0);
  const [joined, setJoined] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/team`,
          authHeaderFromStorage()
        );
        const members = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        if (!cancelled) {
          setTotal(members.length);
          setJoined(joinedThisMonth(members));
        }
      } catch (err) {
        console.error('Failed to fetch employees', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emp-compact">
      <span className="widget-head-iconpill emp-icon" aria-hidden="true">
        <Users size={16} />
      </span>
      <div className="emp-meta">
        <span className="emp-title">Total Employees</span>
        <span className="emp-count">{loading ? '…' : total}</span>
      </div>
      {joined > 0 && <span className="emp-delta">+{joined} this month</span>}
    </div>
  );
};

/* Attendance Summary — live from /api/attendance/company (or /team) ----- */

export const AttendanceSummaryWidget = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      // Company-wide board for attendance managers; everyone else with
      // view_team_attendance gets the same summary scoped to their team.
      const scope = hasPermission('manage_attendance') ? 'company' : 'team';
      try {
        const res = await axios.get(
          `${API_URL}/api/attendance/${scope}`,
          authHeaderFromStorage()
        );
        if (!cancelled) {
          setSummary(res.data?.summary || null);
          setFailed(!res.data?.summary);
        }
      } catch (err) {
        console.error('[attendance-summary] fetch failed', err?.message);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const total = summary?.total || 0;
  const present = (summary?.onTime || 0) + (summary?.earlyBird || 0);
  const absent = summary?.notMarked || 0;
  const late = summary?.late || 0;
  const onLeave = summary?.onLeave || 0;

  // Late arrivals still count as present for the headline figure.
  const presentPct = total > 0 ? Math.round(((present + late) / total) * 100) : 0;

  const rows = [
    { key: 'present', label: 'Present', val: present },
    { key: 'leave', label: 'On leave', val: onLeave },
    { key: 'late', label: 'Late', val: late },
    { key: 'absent', label: 'Absent', val: absent },
  ];

  return (
    <>
      <WidgetHeader icon={Check} title="Today's Attendance" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : failed ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Couldn't load today's attendance.</p>
      ) : (
        <div className="att-sum">
          <div className="att-sum-hero">
            <span className="att-sum-pct">{presentPct}%</span>
            <span className="att-sum-caption">present today</span>
          </div>
          <div className="att-sum-split" aria-hidden="true">
            {rows.map((r) =>
              r.val > 0 ? (
                <span key={r.key} className={`att-sum-seg att--${r.key}`} style={{ flexGrow: r.val }} />
              ) : null
            )}
          </div>
          <ul className="att-sum-rows">
            {rows.map((r) => (
              <li key={r.key} className={`att-sum-row att--${r.key}`}>
                <span>{r.label}</span>
                <b>{r.val}</b>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

/* Performance — live from /api/attendance/analytics ------------------- */

export const PerformanceWidget = () => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/attendance/analytics`,
          authHeaderFromStorage()
        );
        if (!cancelled) {
          setKpis(res.data?.kpis || null);
          setFailed(!res.data?.kpis);
        }
      } catch (err) {
        console.error('[performance-widget] fetch failed', err?.message);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // attendanceRate is % of all staff; latePercentage is % of those present.
  // On-time therefore = present share × the not-late share of the present.
  const attRate = parseFloat(kpis?.attendanceRate || 0);
  const lateRate = parseFloat(kpis?.latePercentage || 0);
  const onTimeRate = Math.max(0, attRate * (1 - lateRate / 100));

  const bars = [
    { label: 'Attendance Rate', pct: attRate, cls: 'att-range', color: '#22c55e' },
    { label: 'On-Time Rate', pct: onTimeRate, cls: 'ontim-range', color: '#6366f1' },
    { label: 'Late Rate', pct: lateRate, cls: 'late-range', color: '#f59e0b' },
  ];

  return (
    <>
      <WidgetHeader icon={TrendingUp} title="Performance" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : failed ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Couldn't load performance data.</p>
      ) : (
        <>
          {kpis?.earlyBirds > 0 && (
            <div style={{ fontSize: 12, marginBottom: 8, opacity: 0.7 }}>
              🌅 {kpis.earlyBirds} early bird{kpis.earlyBirds > 1 ? 's' : ''} today
            </div>
          )}
          {bars.map(({ label, pct, cls, color }) => (
            <div key={cls} className={`item ${cls}`} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <label style={{ fontWeight: 600 }}>{label}</label>
                <span style={{ fontWeight: 700, color }}>{pct.toFixed(1)}%</span>
              </div>
              <div className="range-bar" style={{ height: 7, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }}>
                <div className="bar" style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }}></div>
              </div>
            </div>
          ))}
          {kpis?.onLeaveToday > 0 && (
            <div style={{ fontSize: 12, marginTop: 6, opacity: 0.65 }}>
              ✈️ {kpis.onLeaveToday} on leave today
            </div>
          )}
        </>
      )}
    </>
  );
};

/* Leave balance — remaining days by type for the current user ---------- */
export const LeaveBalanceWidget = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const user = JSON.parse(localStorage.getItem('userdata') || '{}');
      if (!user.id) { setLoading(false); return; }
      try {
        const res = await axios.get(
          `${API_URL}/api/leaves/balance/${user.id}`,
          authHeaderFromStorage()
        );
        if (!cancelled) setBalances(res.data?.balances || []);
      } catch (err) {
        console.error('[leave-balance] fetch failed', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <WidgetHeader icon={Plane} title="My Leave Balance" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : balances.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>No leave types configured.</p>
      ) : (
        <div className="lb-list">
          {balances.map((b) => {
            const max = Number(b.max_days_per_year) || 0;
            const used = Number(b.used_days) || 0;
            const remaining = b.remaining_days != null ? Number(b.remaining_days) : max - used;
            // Bar shows what is still available, so a full bar = untouched.
            const pct = max > 0 ? Math.max(0, Math.min(100, Math.round((remaining / max) * 100))) : 0;
            return (
              <button type="button" key={b.leave_type_id} className="lb-row" onClick={() => navigate('/leaves')}>
                <span className="lb-row-head">
                  <span className="lb-name">
                    {b.name}
                    {b.is_paid === 0 && <span className="lb-unpaid">UNPAID</span>}
                  </span>
                  <b className="lb-val">{remaining} / {max} days</b>
                </span>
                <span className="lb-track">
                  <span className="lb-fill" style={{ width: `${pct}%`, background: b.color || '#94a3b8' }} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

/* Star of the Month --------------------------------------------------- */
// Implementation lives in StarOfMonthWidget.js (themed card + flip + confetti).
export { StarOfTheMonthWidget } from './StarOfMonthWidget';

/* Daily briefing ------------------------------------------------------ */
export { default as DailyBriefingWidget } from './DailyBriefingWidget';

// Build initials for an avatar fallback from a full name (used by the
// Employees-on-Leave widget below).
const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

/* Date / Time + Mark In/Out ------------------------------------------ */

/** Circular worked-hours ring. `frac` 0–1; rotated so it fills from the top. */
const WorkRing = ({ frac, variant }) => {
  const R = 19;
  const C = 2 * Math.PI * R;
  return (
    <svg className={`att-ring att-ring--${variant}`} viewBox="0 0 44 44" aria-hidden="true">
      <circle className="att-ring-track" cx="22" cy="22" r={R} />
      <circle
        className="att-ring-fill"
        cx="22"
        cy="22"
        r={R}
        style={{ strokeDasharray: C, strokeDashoffset: C * (1 - Math.max(0, Math.min(1, frac))) }}
      />
    </svg>
  );
};

export const DateTimeAttendanceWidget = () => {
  const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
  const username = localStorage.getItem('username') || 'Admin';
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const userId = userData.id;

  const apiBase = `${API_URL}/api/attendance`;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayRec, setTodayRec] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(
          `${apiBase}/today/${userId}`,
          authHeaderFromStorage()
        );
        if (!cancelled && res.data) {
          // Normalize datetime strings from server ("YYYY-MM-DD HH:MM:SS") to
          // ISO format so new Date() parses them as local time, not UTC.
          const d = res.data;
          if (d.mark_in) d.mark_in = String(d.mark_in).replace(' ', 'T');
          if (d.mark_out) d.mark_out = String(d.mark_out).replace(' ', 'T');
          setTodayRec(d);
        } else if (!cancelled) {
          setTodayRec(null);
        }
      } catch (err) {
        console.error('Failed to fetch today attendance', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Same rule as the Attendance page's card: in progress = marked in and not
  // yet out. The row's `status` isn't reliable for this — it can already read
  // e.g. 'present'/'late' while the day is still open, which showed the
  // finished view with a Mark In button the server then refused
  // ("Already marked in").
  const attendanceStatus = !todayRec?.mark_in
    ? 'not-marked'
    : !todayRec.mark_out
      ? 'in-progress'
      : 'present';

  const firstName = firstNameOf(userData, username);
  const greeting = greetingFor(currentTime);

  // Clock parts — split so seconds + AM/PM can be styled down independently.
  const h24 = currentTime.getHours();
  const clock = {
    hm: `${h24 % 12 || 12}:${String(currentTime.getMinutes()).padStart(2, '0')}`,
    ss: String(currentTime.getSeconds()).padStart(2, '0'),
    ampm: h24 < 12 ? 'AM' : 'PM',
  };

  // Worked-hours ring — elapsed since mark-in (live while in-progress, frozen
  // at mark-out once present) as a fraction of a standard 8-hour workday.
  const markInDate = todayRec?.mark_in ? new Date(todayRec.mark_in) : null;
  const markOutDate = todayRec?.mark_out ? new Date(todayRec.mark_out) : null;
  const hasIn = markInDate && !isNaN(markInDate.getTime());
  const endRef =
    attendanceStatus === 'present' && markOutDate && !isNaN(markOutDate.getTime())
      ? markOutDate
      : currentTime;
  // Subtract break time, same as AttendanceCard. This matters now that marking
  // in again after a mark-out reopens the day and books the gap as a break —
  // without it the ring would count the hours spent away.
  const breakMs = Number(todayRec?.break_seconds || 0) * 1000;
  const workedMs = hasIn ? Math.max(0, endRef - markInDate - breakMs) : 0;
  const workedLabel = `${Math.floor(workedMs / 3600000)}h ${String(
    Math.floor((workedMs % 3600000) / 60000)
  ).padStart(2, '0')}m`;
  const workedFrac = Math.min(workedMs / (8 * 3600000), 1);

  // Re-read today's row after a failed action so the widget shows the real
  // state (e.g. already marked in from another tab or the Attendance page).
  const refetchToday = async () => {
    try {
      const r2 = await axios.get(`${apiBase}/today/${userId}`, authHeaderFromStorage());
      const d = r2.data || null;
      if (d?.mark_in) d.mark_in = String(d.mark_in).replace(' ', 'T');
      if (d?.mark_out) d.mark_out = String(d.mark_out).replace(' ', 'T');
      setTodayRec(d);
    } catch { }
  };

  // Best-effort location, same as the Attendance page: lets the server check
  // the office radius when location validation is on. Never blocks — gives
  // up after 2.5s and marks in without coordinates.
  const tryGetGeolocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      let done = false;
      const finish = (val) => { if (!done) { done = true; resolve(val); } };
      setTimeout(() => finish({}), 2500);
      navigator.geolocation.getCurrentPosition(
        (pos) => finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => finish({}),
        { enableHighAccuracy: false, timeout: 2500, maximumAge: 300000 }
      );
    });

  const handleMarkIn = async () => {
    if (loadingAttendance) return;
    if (!userId) return toast.error("Couldn't identify your account. Please log in again.");
    setLoadingAttendance(true);
    try {
      const geo = await tryGetGeolocation();
      const res = await axios.post(
        `${apiBase}/mark-in`,
        { user_id: userId, ...geo },
        authHeaderFromStorage()
      );
      // Use the server-returned record so the displayed time matches storage.
      // The server stores mark_in as a local datetime string (e.g. "2026-05-14 09:15:00").
      // Convert space→T so Date() parses it as local time, not UTC.
      const rec = res.data || {};
      const markInIso = rec.mark_in
        ? String(rec.mark_in).replace(' ', 'T')
        : new Date().toISOString();
      setTodayRec({ ...rec, status: 'in-progress', mark_in: markInIso, mark_out: null });
      if (rec.warning) toast.warning(rec.warning);
      else toast.success('Marked in');
    } catch (err) {
      console.error(err);
      // 409 = already marked in: not a failure, just show the real state.
      if (err?.response?.status === 409) toast.info('You are already marked in.');
      else toast.error(err?.response?.data?.error || 'Could not mark in. Please try again.');
      await refetchToday();
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleMarkOut = async () => {
    if (loadingAttendance) return;
    setLoadingAttendance(true);
    try {
      const res = await axios.post(
        `${apiBase}/mark-out`,
        { user_id: userId },
        authHeaderFromStorage()
      );
      const rec = res.data || {};
      const markOutIso = rec.mark_out
        ? String(rec.mark_out).replace(' ', 'T')
        : new Date().toISOString();
      setTodayRec((prev) => ({
        ...prev,
        ...rec,
        status: 'present',
        mark_in: rec.mark_in ? String(rec.mark_in).replace(' ', 'T') : prev?.mark_in,
        mark_out: markOutIso,
      }));
      toast.success('Marked out');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Could not mark out. Please try again.');
      await refetchToday();
    } finally {
      setLoadingAttendance(false);
    }
  };

  return (
    <>
      <div className="date-time">
        <div className="greet-wrap">
          {greeting.text}, <span className="greet-name">{firstName}</span>!{' '}
          <greeting.Icon className="greet-emoji" aria-hidden="true" strokeWidth={2} />
        </div>
        <div className="itme-wrap">
          <span className="t-hm">{clock.hm}</span>
          <span className="t-sec">:{clock.ss}</span>
          <span className="t-ampm">{clock.ampm}</span>
        </div>
        <div className="date-wrap">
          {currentTime.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>
      <div className="att-mark">
        {attendanceStatus === 'not-marked' && (
          <>
            <button onClick={handleMarkIn} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking In...' : 'Mark In'}
            </button>
            <div className="att-pill att-pill--idle">
              <span className="att-pill-dot" />
              Not marked yet
            </div>
          </>
        )}
        {attendanceStatus === 'in-progress' && (
          <>
            <button onClick={handleMarkOut} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking Out...' : 'Mark Out'}
            </button>
            <div className="att-progress">
              <WorkRing frac={workedFrac} variant="active" />
              <div className="att-progress-info">
                <span className="att-progress-time">{workedLabel}</span>
                <span className="att-progress-sub">
                  since {fmtTime(todayRec?.mark_in)}
                </span>
              </div>
            </div>
          </>
        )}
        {attendanceStatus === 'present' && (
          <>
            {/* Marking out doesn't close the day — someone who stepped out (or
                marked out by mistake) can mark in again. The server reopens
                today's row and books the gap as a break, so hours stay right. */}
            <button onClick={handleMarkIn} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking In...' : 'Mark In'}
            </button>
            <div className="att-progress">
              <WorkRing frac={workedFrac} variant="done" />
              <div className="att-progress-info">
                <span className="att-progress-time">{workedLabel}</span>
                <span className="att-progress-sub">
                  {fmtTime(todayRec?.mark_in)}–{fmtTime(todayRec?.mark_out)}
                </span>
              </div>
            </div>
          </>
        )}
        <button
          type="button"
          className="att-link"
          onClick={() => (window.location.href = `/${role}/attendance`)}
        >
          View full attendance
          <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
};

/* Personal Notes ------------------------------------------------------ */

// Dashboard preview of the user's notes: shows the pinned (or most-recent)
// note and links to the full Notes page. Full CRUD lives on /notes.
const notesBasePath = () => {
  const role = localStorage.getItem('role');
  if (role === 'Admin') return '/admin';
  if (role === 'HR') return '/hr';
  return '/member';
};
const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

// "10 Jun" — the note's automatic created-date tag (short form for stickies).
const noteDateTag = (ts) => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// Extract checklist items from a note body (TipTap taskList HTML).
// Returns null when the note has no checklist so callers fall back to a snippet.
const parseTaskItems = (html) => {
  if (!html || !html.includes('data-type="taskList"')) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const items = Array.from(doc.querySelectorAll('ul[data-type="taskList"] > li'));
  if (!items.length) return null;
  return items.map((li) => ({
    checked: li.getAttribute('data-checked') === 'true',
    text: (li.textContent || '').replace(/\s+/g, ' ').trim(),
  }));
};

// Flip the nth checklist item in the note body and return the updated HTML.
const toggleTaskInHtml = (html, index) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const items = doc.querySelectorAll('ul[data-type="taskList"] > li');
  const li = items[index];
  if (!li) return html;
  const next = li.getAttribute('data-checked') !== 'true';
  li.setAttribute('data-checked', String(next));
  const input = li.querySelector('input[type="checkbox"]');
  if (input) {
    if (next) input.setAttribute('checked', 'checked');
    else input.removeAttribute('checked');
  }
  return doc.body.innerHTML;
};

// Paper tint for a sticky: the note's label color washed into warm paper.
const stickyPaper = (color) =>
  color
    ? `color-mix(in srgb, ${color} 14%, #fffdf2)`
    : '#fff9db';

// One sticky note on the wall. Checklist items are live — ticking saves.
const StickyNoteCard = ({ note, onOpen, onToggleTask }) => {
  const tasks = parseTaskItems(note.body);
  const shown = tasks ? tasks.slice(0, 3) : null;
  const doneCount = tasks ? tasks.filter((t) => t.checked).length : 0;
  const snippet = !tasks ? stripHtml(note.body) : '';

  return (
    <div
      role="button"
      tabIndex={0}
      className="pn-sticky"
      style={{ '--pn-paper': stickyPaper(note.color) }}
      onClick={() => onOpen(note.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(note.id); }}
    >
      {!!note.pinned && (
        <span className="pn-sticky-pin" title="Pinned">
          <Pin size={12} />
        </span>
      )}
      <div className="pn-sticky-title">{note.title || 'Untitled note'}</div>

      {shown ? (
        <div className="pn-sticky-tasks">
          {shown.map((t, i) => (
            <label
              key={i}
              className={`pn-task${t.checked ? ' done' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={t.checked}
                onChange={() => onToggleTask(note, i)}
              />
              <span>{t.text || 'To-do'}</span>
            </label>
          ))}
          {tasks.length > 3 && (
            <span className="pn-task-more">+{tasks.length - 3} more</span>
          )}
        </div>
      ) : (
        <div className="pn-sticky-snippet">{snippet || 'No content yet'}</div>
      )}

      <div className="pn-sticky-foot">
        <span className="pn-sticky-date">{noteDateTag(note.created_at)}</span>
        {tasks && tasks.length > 0 && (
          <span className={`pn-sticky-count${doneCount === tasks.length ? ' all-done' : ''}`}>
            {doneCount}/{tasks.length}
          </span>
        )}
      </div>
    </div>
  );
};

export const PersonalNotesWidget = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await notesApi.list();
      setNotes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('[personal-notes] fetch failed', err?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    notesApi
      .list()
      .then((list) => !cancelled && setNotes(Array.isArray(list) ? list : []))
      .catch((err) => console.warn('[personal-notes] fetch failed', err?.message))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Backend returns pinned-first, then most-recent. Wall shows the first 4.
  const wall = notes.slice(0, 4);
  const goNotes = () => navigate(`${notesBasePath()}/notes`);
  const openNote = (id) => navigate(`${notesBasePath()}/notes?note=${id}`);

  // Tick a checklist item straight from the wall — optimistic, then save.
  const handleToggleTask = async (note, taskIndex) => {
    const nextHtml = toggleTaskInHtml(note.body, taskIndex);
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, body: nextHtml } : n)));
    try {
      await notesApi.update(note.id, { body: nextHtml });
    } catch (err) {
      console.error('[personal-notes] task toggle failed', err);
      refresh(); // roll back to server truth
    }
  };

  // Inline quick-add: create from the title field and stay on the dashboard.
  const handleQuickAdd = async (e) => {
    e.preventDefault();
    const title = draft.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      await notesApi.create({ title, body: '' });
      setDraft('');
      await refresh();
    } catch (err) {
      console.error('[personal-notes] quick-add failed', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <WidgetHeader
        icon={StickyNote}
        title="Personal Notes"
        action={
          <span className="pn-head-actions">
            <button type="button" className="pn-open" title="Open Notes" onClick={goNotes}>
              Open <ChevronRight size={13} />
            </button>
          </span>
        }
      />
      <div className="content personal-notes-content">
        {/* Inline quick-add */}
        <form className="pn-quickadd" onSubmit={handleQuickAdd}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Quick note…"
            aria-label="Quick note title"
          />
          <button type="submit" className="pn-quickadd-btn" disabled={!draft.trim() || adding} title="Add note">
            <Plus size={16} />
          </button>
        </form>

        {!loaded ? (
          <div className="pn-empty">Loading your notes…</div>
        ) : wall.length === 0 ? (
          <div className="pn-empty">
            <StickyNote size={20} color="#f54b01" />
            No notes yet — jot one above.
          </div>
        ) : (
          <>
            <div className="pn-wall">
              {wall.map((n) => (
                <StickyNoteCard
                  key={n.id}
                  note={n}
                  onOpen={openNote}
                  onToggleTask={handleToggleTask}
                />
              ))}
            </div>
            {notes.length > wall.length && (
              <button type="button" className="pn-more" onClick={goNotes}>
                +{notes.length - wall.length} more note{notes.length - wall.length === 1 ? '' : 's'}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};


/* Employees on Leave Today ------------------------------------------- */

export const EmployeesOnLeaveWidget = () => {
  const [todayLeaves, setTodayLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/leaves/all`,
          authHeaderFromStorage()
        );
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const filtered = (res.data || []).filter((leave) => {
          if (leave.status !== 'Approved') return false;
          const start = new Date(leave.start_date);
          const end = new Date(leave.end_date);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          return today >= start && today <= end;
        });
        if (!cancelled) setTodayLeaves(filtered);
      } catch (err) {
        console.error("Failed to fetch today's leaves", err);
        if (!cancelled) setTodayLeaves([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const count = todayLeaves.length;

  // Accent per leave type: the type's own colour when set, else by keyword.
  // `type` is null when the leave's type has since been deleted (the list
  // LEFT JOINs leave_types) — a default parameter only covers undefined.
  const leaveAccent = (type, color) => {
    if (color) return color;
    const t = String(type || '').toLowerCase();
    if (/sick|medical/.test(t)) return '#e11d48';
    if (/casual/.test(t)) return '#2563eb';
    if (/vacation|annual|earned/.test(t)) return '#16a34a';
    if (/comp/.test(t)) return '#9333ea';
    if (/maternity|paternity|parental/.test(t)) return '#db2777';
    if (/wfh|remote|work[- ]from/.test(t)) return '#7c3aed';
    return '#d97706';
  };

  // "Casual Leave" → "Casual" for the compact pill.
  const shortType = (type) =>
    String(type || 'Leave').replace(/\s+leave$/i, '') || 'Leave';

  const handleSeeAll = () => {
    window.location.href = `/${role}/leaves`;
  };

  return (
    <div className="event-panel">
      <SectionHeader
        icon={Plane}
        title="Employees on Leave Today"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading…</p>
      ) : count > 0 ? (
        <div className="event-list">
          {todayLeaves.map((leave) => (
            <div
              className="person-row"
              key={leave.id}
              style={{ '--accent': leaveAccent(leave.leave_type, leave.leave_color) }}
            >
              <span className="person-row-avatar" aria-hidden="true">
                {initialsOf(leave.employee_name || '')}
              </span>
              <div className="person-row-body">
                <h5 className="person-row-title">{leave.employee_name}</h5>
                {(leave.department_name || leave.designation) && (
                  <p className="person-row-sub">
                    {leave.department_name || leave.designation}
                  </p>
                )}
              </div>
              <span className="person-row-pill">{shortType(leave.leave_type)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="leave-empty">
          <div className="leave-empty-icon" aria-hidden="true">✅</div>
          <b>All hands on deck</b>
          <p>No employees on leave today</p>
        </div>
      )}
    </div>
  );
};

/* =====================================================================
   Upcoming Holidays & Celebrations — shared helpers
   ===================================================================== */

const MONTH_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Days between two dates (ignoring time-of-day).
const daysUntil = (isoDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const countdownLabel = (isoDate) => {
  const d = daysUntil(isoDate);
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  if (d < 7) return `in ${d} days`;
  if (d < 14) return 'Next week';
  if (d < 31) return `in ${Math.round(d / 7)} wks`;
  return `in ${Math.round(d / 30)} mo`;
};

const dateChip = (isoDate) => {
  const d = new Date(isoDate);
  return { day: d.getDate(), mon: MONTH_SHORT[d.getMonth()] };
};

// Emoji matcher — falls back to category, then default.
const HOLIDAY_EMOJI_MAP = [
  [/christmas|xmas/i, '🎄', '#10b981'],
  [/new\s*year/i, '🎆', '#6366f1'],
  [/diwali|deepavali/i, '🪔', '#f59e0b'],
  [/holi/i, '🎨', '#ec4899'],
  [/eid/i, '🌙', '#14b8a6'],
  [/easter/i, '🐣', '#f472b6'],
  [/thanksgiving/i, '🦃', '#d97706'],
  [/halloween/i, '🎃', '#f97316'],
  [/valentine/i, '💝', '#ef4444'],
  [/independence|republic|national/i, '🎌', '#0ea5e9'],
  [/labor|labour/i, '🛠️', '#64748b'],
];

const pickHolidayStyle = (name) => {
  for (const [rx, emoji, color] of HOLIDAY_EMOJI_MAP) {
    if (rx.test(name)) return { emoji, color };
  }
  return { emoji: '📅', color: '#0ea5e9' };
};

const CELEBR_EMOJI_MAP = [
  [/birthday|b-?day/i, '🎂', '#ec4899'],
  [/anniversary/i, '🏆', '#f59e0b'],
  [/wedding|marriage/i, '💒', '#f472b6'],
  [/milestone|goal/i, '🎯', '#8b5cf6'],
  [/promotion|promoted/i, '⭐', '#0ea5e9'],
  [/achievement|award/i, '🏅', '#eab308'],
  [/baby|newborn/i, '👶', '#22d3ee'],
  [/retire/i, '🎖️', '#64748b'],
];

const pickCelebrationStyle = (name) => {
  for (const [rx, emoji, color] of CELEBR_EMOJI_MAP) {
    if (rx.test(name)) return { emoji, color };
  }
  return { emoji: '🎉', color: '#f54900' };
};

/**
 * One upcoming event: emoji, title + subtitle, and a day/month date chip.
 * The nearest event (`hero`) sits on a frosted card; the rest are plain rows.
 * The countdown ("in 3 days") is the chip's tooltip.
 */
const EventRow = ({ emoji, accent, title, subtitle, isoDate, hero = false }) => {
  const { day, mon } = dateChip(isoDate);
  return (
    <div className={`event-hero${hero ? '' : ' event-hero--plain'}`} style={{ '--accent': accent }}>
      <span className="event-hero-emoji" aria-hidden="true">{emoji}</span>
      <div className="event-hero-mid">
        <h4 className="event-hero-title">{title}</h4>
        {subtitle && <p className="event-hero-sub">{subtitle}</p>}
      </div>
      <div className="event-hero-chip" title={countdownLabel(isoDate)}>
        <b>{String(day).padStart(2, '0')}</b>
        <span>{mon}</span>
      </div>
    </div>
  );
};

/** Shared header with built-in "See all ›" button. */
const SectionHeader = ({ icon, title, onSeeAll }) => (
  <div className="widget-head-row">
    <WidgetHeader icon={icon} title={title} />
    <button
      type="button"
      className="widget-head-seeall"
      onClick={onSeeAll}
    >
      See all <ChevronRight size={14} />
    </button>
  </div>
);

/* Upcoming Holidays --------------------------------------------------- */

/**
 * Shift a recurring holiday forward by whole years until its date is on/after
 * today. Non-recurring holidays pass through unchanged. Returns ISO YYYY-MM-DD.
 */
const effectiveHolidayDate = (isoDate, repeatAnnually) => {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;

  if (repeatAnnually) {
    while (d < today) d.setFullYear(d.getFullYear() + 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const UpcomingHolidaysWidget = () => {
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_URL}/api/settings/calendar/holidays`,
          authHeaderFromStorage()
        );
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const normalised = list
          .map((h) => {
            const date = effectiveHolidayDate(h.date, !!h.repeat_annually);
            return date
              ? {
                id: h.id,
                name: h.name,
                type: h.type,
                date,
              }
              : null;
          })
          .filter(Boolean)
          .filter((h) => daysUntil(h.date) >= 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setHolidays(normalised);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const [hero, ...rest] = holidays;
  const handleSeeAll = () => {
    window.location.href = `/${role}/calendar`;
  };

  return (
    <div className="event-panel">
      <SectionHeader
        icon={Calendar}
        title="Upcoming Holidays"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading holidays…</p>
      ) : error ? (
        <p className="event-empty">Couldn't load holidays.</p>
      ) : hero ? (
        <div className="event-list">
          <EventRow
            hero
            {...pickHolidayStyle(hero.name)}
            title={hero.name}
            subtitle={hero.type}
            isoDate={hero.date}
          />
          {rest.map((h) => (
            <EventRow
              key={h.id}
              {...pickHolidayStyle(h.name)}
              title={h.name}
              subtitle={h.type}
              isoDate={h.date}
            />
          ))}
        </div>
      ) : (
        <p className="event-empty">No upcoming holidays.</p>
      )}
    </div>
  );
};

/* Upcoming Celebrations ---------------------------------------------- */

/**
 * Next occurrence (today or later) of a recurring month/day. Returns the ISO
 * date and its year, so anniversaries can count completed years from it.
 */
const nextOccurrence = (month, day) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const candidate = new Date(today.getFullYear(), month - 1, day);
  if (candidate < today) candidate.setFullYear(candidate.getFullYear() + 1);
  const y = candidate.getFullYear();
  const m = String(candidate.getMonth() + 1).padStart(2, '0');
  const d = String(candidate.getDate()).padStart(2, '0');
  return { isoDate: `${y}-${m}-${d}`, year: y };
};

export const UpcomingCelebrationsWidget = () => {
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        // Same feed as the Calendar: active employees' birthdays (dob) and
        // work anniversaries (doj, falling back to the legacy join_date),
        // already split into month/day — open to every signed-in user.
        const res = await axios.get(
          `${API_URL}/api/settings/calendar/celebrations`,
          authHeaderFromStorage()
        );
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const events = [];
        rows.forEach((c) => {
          if (!c.month || !c.day) return;
          const next = nextOccurrence(c.month, c.day);
          if (c.type === 'birthday') {
            events.push({
              id: c.id,
              // The feed labels birthdays "Name · Birthday"; the row shows the
              // name and puts the occasion underneath.
              name: String(c.name || '').replace(/\s*·\s*Birthday$/, ''),
              type: 'Birthday',
              date: next.isoDate,
            });
          } else if (c.type === 'anniversary') {
            const years = next.year - Number(c.source_year);
            if (!(years > 0)) return; // joining year itself isn't an anniversary
            events.push({
              id: c.id,
              name: c.name,
              type: `${years} Year${years > 1 ? 's' : ''} Work Anniversary`,
              date: next.isoDate,
            });
          }
        });

        const normalised = events
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, 4);
        setItems(normalised);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const [hero, ...rest] = items;
  const handleSeeAll = () => {
    window.location.href = `/${role}/calendar`;
  };

  return (
    <div className="event-panel">
      <SectionHeader
        icon={PartyPopper}
        title="Upcoming Celebrations"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading celebrations…</p>
      ) : error ? (
        <p className="event-empty">Couldn't load celebrations.</p>
      ) : hero ? (
        <div className="event-list">
          <EventRow
            hero
            {...pickCelebrationStyle(hero.name + ' ' + hero.type)}
            title={hero.name}
            subtitle={hero.type}
            isoDate={hero.date}
          />
          {rest.map((c) => (
            <EventRow
              key={c.id}
              {...pickCelebrationStyle(c.name + ' ' + c.type)}
              title={c.name}
              subtitle={c.type}
              isoDate={c.date}
            />
          ))}
        </div>
      ) : (
        <p className="event-empty">No upcoming celebrations.</p>
      )}
    </div>
  );
};
